import styled from 'styled-components';

import { COLORS, CASINO_SETTINGS, MOBILE, ANIM } from '../../../../theme/tokens';
import { Text } from '../../../../components';

export const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const Card = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px 18px;
  border-radius: 14px;
  /* Standard grey card surface (surfaceFaint) so it reads on both the dark and the
     white (light) panel — surfaceSoft resolved to the panel's own white on light and
     vanished. The card isn't clickable, so it carries no hover wash. */
  background: ${COLORS.surfaceFaint};
`;

export const Head = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
`;

export const HeadLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
`;

export const DomainName = styled(Text)`
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.textStrong};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const PrimaryTag = styled(Text)`
  flex: 0 0 auto;
  padding: 2px 8px;
  border-radius: 99px;
  border: 1px solid ${COLORS.hairline};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${COLORS.textMuted};
`;

export const StatusTag = styled.span<{ $bg: string; $fg: string }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 24px;
  padding: 0 10px;
  border-radius: 99px;
  font-size: 11px;
  font-weight: 700;
  color: ${(p) => p.$fg};
  background: ${(p) => p.$bg};
  white-space: nowrap;
`;

export const TypeRow = styled.div`
  display: flex;
`;

export const TypeTag = styled.span<{ $fg: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 26px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid ${COLORS.hairline};
  /* Steps to the panel surface so the pill stays distinct from the grey card. */
  background: ${COLORS.surfaceCard};
  font-size: 12px;
  font-weight: 600;
  color: ${(p) => p.$fg};
`;

export const Meta = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px 18px;

  ${MOBILE} {
    grid-template-columns: 1fr;
  }
`;

export const MetaItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

export const MetaLabel = styled(Text)`
  font-size: 11px;
  color: ${COLORS.textFaint};
`;

export const MetaValue = styled(Text)`
  font-size: 14px;
  color: ${COLORS.featureName};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Divider = styled.div`
  height: 1px;
  background: ${COLORS.hairline};
`;

export const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export const ManagedNote = styled(Text)`
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 300;
  color: ${COLORS.textMuted};
`;

/** "Visit site" link button — an <a> dressed as the primary (filled) Button (same pill /
 *  48px height / solid CTA fill) so it matches the "Save changes" CTA on the other cards,
 *  but actually navigates to the live casino in a new tab. `margin-left: auto` pins it to
 *  the right of the footer, with the note sitting on the left. */
export const VisitLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  box-sizing: border-box;
  height: 48px;
  margin-left: auto;
  padding: 0 18px;
  border-radius: 99px;
  border: 1px solid ${COLORS.ctaSolid};
  background: ${COLORS.ctaSolid};
  color: ${COLORS.ctaSolidText};
  font-size: 16px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  transition: filter ${ANIM.INTERACT_MS}ms ease;

  &:hover,
  &:focus-visible {
    filter: brightness(0.95);
  }
`;

export const ActionNote = styled(Text)`
  font-size: 12px;
  font-weight: 400;
  color: ${CASINO_SETTINGS.lockFg};
`;

/** Neutral "Unknown" pill — shown on the unknown-state card when no real domain record
 *  is available. Deliberately uncoloured (outline + muted ink) so it never reads as a
 *  live "Active"/"Action required" status. */
export const UnknownTag = styled.span`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  height: 24px;
  padding: 0 10px;
  border-radius: 99px;
  border: 1px solid ${COLORS.hairline};
  background: ${COLORS.surfaceCard};
  font-size: 11px;
  font-weight: 700;
  color: ${COLORS.textMuted};
  white-space: nowrap;
`;

/** Muted explanatory line on the unknown-state card. */
export const UnknownNote = styled(Text)`
  font-size: 13px;
  font-weight: 300;
  color: ${COLORS.textMuted};
`;

export const SkelStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;
